import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthProvider';
import { FormError } from '@/components/common/FormError';
import { FormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Email only, "Email me a link" (design section 2): SignIn (`createUser: false`, enumeration safe: a
 * "no such account" answer reads as sent) and the invite page (`createUser: true`). GoTrue's answer is
 * shown through its `auth.errors.*` key; its raw English text only reaches console.warn (AuthProvider).
 */
export function SignInLinkForm({
  redirectPath,
  createUser,
  onSent,
  idPrefix = 'link',
  email: controlledEmail,
  onEmailChange,
}: {
  /** A portal path the link brings the person back to ('/signin', '/invite/<id>'). */
  redirectPath: string;
  createUser: boolean;
  /** Called with the normalized address once GoTrue accepted the request (or, enumeration safe, refused it). */
  onSent(email: string): void;
  idPrefix?: string;
  /** Controlled address (SignIn shares one field across its modes); uncontrolled when absent. */
  email?: string;
  onEmailChange?(value: string): void;
}) {
  const { t } = useTranslation();
  const { sendSignInLink } = useAuth();
  const [ownEmail, setOwnEmail] = React.useState('');
  const email = controlledEmail ?? ownEmail;
  const setEmail = onEmailChange ?? setOwnEmail;
  const [fieldError, setFieldError] = React.useState<string | null>(null);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setServerError(null);
    const address = email.trim().toLowerCase();
    if (!address) {
      setFieldError(t('auth.link.emailRequired'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+$/.test(address)) {
      setFieldError(t('auth.errors.invalidEmail'));
      return;
    }
    setFieldError(null);
    setBusy(true);
    const { error } = await sendSignInLink(address, redirectPath, { createUser });
    setBusy(false);
    if (error) {
      setServerError(t(`auth.errors.${error}`));
      return;
    }
    onSent(address);
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
      <FormError message={serverError} />
      <FormField id={`${idPrefix}-email`} label={t('auth.email')} error={fieldError}>
        <Input
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder={t('auth.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </FormField>
      <Button type="submit" disabled={busy}>
        {busy ? t('auth.link.sending') : t('auth.link.submit')}
      </Button>
    </form>
  );
}
